import { Box, Button, Checkmark, createTreeCollection, TreeView } from '@chakra-ui/react';
import VButton from '../VButton';
import VInput from '../VInput';
import VPopover from '../VPopover';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';

export type VTreeSelectItem = {
  id: string;
  label: string;
  children?: VTreeSelectItem[];
};

export type VTreeSelectProps = {
  items: VTreeSelectItem[];
  value: string[];
  onCommit: (ids: string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  id?: string;
  startElement?: ReactNode;
};

type VTreeSelectCollection = ReturnType<typeof createTreeCollection<VTreeSelectItem>>;

const createCollection = (items: VTreeSelectItem[]): VTreeSelectCollection =>
  createTreeCollection<VTreeSelectItem>({
    rootNode: { id: 'tree-select-root', label: '', children: items },
    nodeToValue: (node) => node.id,
    nodeToString: (node) => node.label,
    nodeToChildren: (node) => node.children ?? [],
  });

const getPathLabel = (collection: VTreeSelectCollection, id: string): string => {
  const indexPath = collection.getIndexPath(id);
  if (indexPath === undefined) {
    return '';
  }
  return collection
    .getValuePath(indexPath)
    .map((value) => collection.findNode(value)?.label ?? '')
    .join(' / ');
};

const isFullyChecked = (
  collection: VTreeSelectCollection,
  item: VTreeSelectItem,
  ids: string[]
): boolean => collection.getDescendantValues(item.id).every((leafId) => ids.includes(leafId));

const getCheckedLabels = (
  collection: VTreeSelectCollection,
  items: VTreeSelectItem[],
  ids: string[]
): string[] =>
  items.flatMap((item) => {
    if (item.children === undefined || item.children.length === 0) {
      return ids.includes(item.id) ? [item.label] : [];
    }
    if (isFullyChecked(collection, item, ids)) {
      return [`${item.label} (all)`];
    }
    return getCheckedLabels(collection, item.children, ids);
  });

const getSelectionLabel = (
  collection: VTreeSelectCollection,
  items: VTreeSelectItem[],
  ids: string[],
  multiple: boolean
): string => {
  if (multiple) {
    return getCheckedLabels(collection, items, ids).join(', ');
  }
  return ids.length === 0 ? '' : getPathLabel(collection, ids[0]);
};

const getAncestorIds = (collection: VTreeSelectCollection, ids: string[]): string[] =>
  ids.flatMap((id) => collection.getParentNodes(id).map((node) => node.id));

const matchesQuery = (node: VTreeSelectItem, query: string): boolean =>
  node.label.toLowerCase().includes(query.toLowerCase());

const haveSameMembers = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((id) => right.includes(id));

const NodeCheckbox = ({ checked }: { checked: boolean | 'indeterminate' }): ReactElement => (
  <TreeView.NodeCheckbox>
    <Checkmark
      checked={checked === true}
      indeterminate={checked === 'indeterminate'}
      size="sm"
      colorPalette="primary"
    />
  </TreeView.NodeCheckbox>
);

const VTreeSelect = ({
  items,
  value,
  onCommit,
  multiple = false,
  placeholder = 'Select',
  readOnly = false,
  disabled = false,
  id,
  startElement,
}: VTreeSelectProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<string[]>(value);
  const [expanded, setExpanded] = useState<string[]>([]);

  const collection = createCollection(items);
  const visibleCollection =
    query === '' ? collection : collection.filter((node) => matchesQuery(node, query));
  const expandedValue = query === '' ? expanded : visibleCollection.getBranchValues();
  const label = getSelectionLabel(collection, items, value, multiple);

  const commit = (ids: string[]): void => {
    if (!haveSameMembers(ids, value)) {
      onCommit(ids);
    }
  };

  const openPicker = (): void => {
    setPending(value);
    setQuery('');
    setExpanded(getAncestorIds(collection, value));
    setOpen(true);
  };

  const closePicker = (): void => {
    if (multiple) {
      commit(pending);
    }
    setOpen(false);
  };

  const onOpenChange = ({ open: nextOpen }: { open: boolean }): void => {
    if (nextOpen) {
      openPicker();
      return;
    }
    closePicker();
  };

  const onSingleSelection = ({ selectedValue }: { selectedValue: string[] }): void => {
    commit(selectedValue);
    setOpen(false);
  };

  const onClearAll = (): void => {
    if (!multiple) {
      commit([]);
      setOpen(false);
      return;
    }
    setPending([]);
  };

  const onQueryChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setQuery(event.target.value);

  const renderNode = ({
    node,
    nodeState,
  }: {
    node: VTreeSelectItem;
    nodeState: { isBranch: boolean; checked: boolean | 'indeterminate' };
  }): ReactElement => {
    if (nodeState.isBranch) {
      return (
        <TreeView.BranchControl>
          {multiple && <NodeCheckbox checked={nodeState.checked} />}
          <TreeView.BranchText>{node.label}</TreeView.BranchText>
          <TreeView.BranchTrigger>
            <TreeView.BranchIndicator>
              <LuChevronRight />
            </TreeView.BranchIndicator>
          </TreeView.BranchTrigger>
        </TreeView.BranchControl>
      );
    }
    return (
      <TreeView.Item>
        {multiple && <NodeCheckbox checked={nodeState.checked} />}
        <TreeView.ItemText>{node.label}</TreeView.ItemText>
      </TreeView.Item>
    );
  };

  const tree = multiple ? (
    <TreeView.Root
      collection={visibleCollection}
      checkedValue={pending}
      onCheckedChange={({ checkedValue }) => setPending(checkedValue)}
      expandedValue={expandedValue}
      onExpandedChange={({ expandedValue: next }) => setExpanded(next)}
      size="sm"
    >
      <TreeView.Tree aria-label="select">
        <TreeView.Node render={renderNode} />
      </TreeView.Tree>
    </TreeView.Root>
  ) : (
    <TreeView.Root
      collection={visibleCollection}
      selectionMode="single"
      selectedValue={value}
      onSelectionChange={onSingleSelection}
      expandedValue={expandedValue}
      onExpandedChange={({ expandedValue: next }) => setExpanded(next)}
      expandOnClick={false}
      size="sm"
    >
      <TreeView.Tree aria-label="select">
        <TreeView.Node render={renderNode} />
      </TreeView.Tree>
    </TreeView.Root>
  );

  const body = (
    <Box display="flex" flexDirection="column" gap="2">
      <VInput
        type="search"
        placeholder="Search"
        aria-label="search"
        value={query}
        onChange={onQueryChange}
      />
      <Box maxHeight="20rem" overflowY="auto">
        {tree}
      </Box>
    </Box>
  );

  const footer = (
    <Box display="flex" justifyContent="space-between" width="full">
      <VButton color="tertiary" size="small" onClick={onClearAll}>
        Clear all
      </VButton>
      <VButton size="small" onClick={closePicker}>
        Done
      </VButton>
    </Box>
  );

  const trigger = (
    <Button
      id={id}
      variant="outline"
      size="sm"
      borderRadius="md"
      width="full"
      justifyContent="space-between"
      fontWeight="normal"
      bg={readOnly ? 'bg.muted' : undefined}
      color={label === '' ? 'fg.muted' : undefined}
      disabled={disabled || readOnly}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      {startElement}

      <Box textAlign={'start'} as="span" flex="1" minWidth="0" truncate>
        {label === '' ? placeholder : label}
      </Box>
      <Box as="span" flexShrink="0" display="flex">
        <LuChevronDown />
      </Box>
    </Button>
  );

  return (
    <VPopover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      body={body}
      footer={footer}
      width="100%"
      showArrow={false}
      defaultOpen={false}
      title=""
      positioning={{ sameWidth: true }}
    ></VPopover>
  );
};

export default VTreeSelect;
